import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Skeleton, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { websiteOwnerTypeForWorkspace } from "./websiteUtils";
import "./OlreadiaFlowPages.css";
import { OLIVEEB_COLORS } from "workspaces/colors";
import PreviewModal from "components/websites/PreviewModal";
// import { OLIVEEB_COLORS } from "utils/styles";

const { Paragraph, Text, Title } = Typography;

const C = {
  bg: "#ffffff",
  bgSurface: "#f7f6f3",
  border: "rgba(0,0,0,0.10)",
  borderMid: "rgba(0,0,0,0.16)",
  text: "#1a1a1a",
  textMuted: "#6b6b6b",
  textFaint: "#a0a0a0",
  blue: "#3b82f6",
  blueBg: "#eff6ff",
};

// template card
function TemplateCard({ template, onPreview, onUse }) {
  const [hovered, setHovered] = useState(false);

  const isPortfolio = template.type === "PORTFOLIO";

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: `1.5px solid ${hovered ? C.borderMid : C.border}`,
        borderRadius: 12,
        overflow: "hidden",
        background: C.bg,
        boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.08)" : "none",
        transition: "all 0.15s",
      }}
    >
      {/* thumbnail */}
      <div
        onClick={() => onPreview(template)}
        style={{
          height: 160,
          background: C.bgSurface,
          backgroundImage: template.thumbnailUrl
            ? `url(${template.thumbnailUrl})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "top center",
          cursor: "pointer",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {!template.thumbnailUrl && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="ti ti-layout-grid"
              style={{ fontSize: 32, color: C.textFaint, opacity: 0.3 }}
            />
          </div>
        )}
        {hovered && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <i className="ti ti-eye" style={{ fontSize: 14, color: "#fff" }} />
            <span style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>
              Preview
            </span>
          </div>
        )}
      </div>

      {/* info */}
      <div style={{ padding: "12px 14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {template.name}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              padding: "2px 7px",
              borderRadius: 4,
              background: isPortfolio ? "#f5f3ff" : C.blueBg,
              color: isPortfolio ? "#7c3aed" : C.blue,
            }}
          >
            {isPortfolio ? "Portfolio" : "Business"}
          </span>
        </div>

        <p
          style={{
            fontSize: 11,
            color: C.textMuted,
            margin: "0 0 12px",
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {template.description}
        </p>

        <button
          onClick={() => onUse(template)}
          style={{
            width: "100%",
            padding: "7px 0",
            border: "none",
            borderRadius: 7,
            color: "#fff",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen} 0%, ${OLIVEEB_COLORS.oliveebDarkGreen} 100%)`,
          }}
        >
          Use this template
        </button>
      </div>
    </div>
  );
}

// skeleton card — matches the pulse loader in TemplateMarketplacePage
function SkeletonCard() {
  return (
    <div
      style={{
        height: 260,
        borderRadius: 12,
        background: C.bgSurface,
        animation: "pulse 1.5s ease-in-out infinite",
      }}
    />
  );
}

export default function WebsiteTemplates({ regenerate = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedWorkspace, isOrganizationWorkspace } = useWorkspace();

  const actorId = user?.id || user?.userId || "";
  const websiteId = searchParams.get("websiteId") || "";
  const ownerType = websiteOwnerTypeForWorkspace({
    isOrganizationWorkspace,
    selectedWorkspace,
    searchOwnerType: searchParams.get("ownerType"),
  });

  const [templates, setTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [createTemplate, setCreateTemplate] = useState(null);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setTemplates(
        await olreadiaService.websites.templates({ type: ownerType, actorId }),
      );
    } catch (nextError) {
      setError(nextError?.message || "Could not load website templates.");
    } finally {
      setLoading(false);
    }
  }, [actorId, ownerType]);

  useEffect(() => {
    setCategory("All");
    load();
  }, [load, ownerType]);

  const categories = useMemo(
    () => [
      "All",
      ...Array.from(new Set(templates.map((t) => t.category).filter(Boolean))),
    ],
    [templates],
  );

  const filtered = useMemo(
    () =>
      category === "All"
        ? templates
        : templates.filter((t) => t.category === category),
    [category, templates],
  );

  const handlePreview = (template) => {
    const url =
      template.previewUrl ||
      olreadiaService.websites.templatePreviewUrl(template.id);
    window.open(url, "_blank");
  };

  const handleUse = (template) => {
    const nextParams = new URLSearchParams({ ownerType });
    if (regenerate && websiteId) {
      nextParams.set("websiteId", websiteId);
      nextParams.set("mode", "regenerate");
    }
    navigate(`${paths.websiteBuild(template.id)}?${nextParams.toString()}`);
  };

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          {/* header */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: C.text,
                    margin: 0,
                    marginBottom: 3,
                  }}
                >
                  {regenerate ? "Update Template" : "Choose a template"}
                </h1>
                <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>
                  Preview{" "}
                  {ownerType === "PORTFOLIO"
                    ? "personal portfolio"
                    : "organization"}{" "}
                  layouts and pick a starting point for your site
                </p>
              </div>
              <button
                onClick={load}
                disabled={loading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  border: `0.5px solid ${C.border}`,
                  borderRadius: 7,
                  background: C.bgSurface,
                  fontSize: 12,
                  color: C.textMuted,
                  cursor: loading ? "default" : "pointer",
                  fontFamily: "inherit",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <i
                  className={`ti ti-refresh${loading ? " spin" : ""}`}
                  style={{ fontSize: 13 }}
                />
                Refresh
              </button>
            </div>

            {/* category filter pills */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {categories.map((item) => (
                <button
                  key={item}
                  onClick={() => setCategory(item)}
                  style={{
                    padding: "5px 12px",
                    border: `0.5px solid ${C.border}`,
                    borderRadius: 99,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 0.12s",
                    background: category === item ? C.text : C.bgSurface,
                    color: category === item ? "#fff" : C.textMuted,
                  }}
                >
                  {item === "All" ? "All" : item.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>

          {/* error */}
          {error && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderRadius: 8,
                background: "#fef2f2",
                border: "0.5px solid rgba(239,68,68,0.3)",
                marginBottom: 16,
              }}
            >
              <i
                className="ti ti-alert-circle"
                style={{ fontSize: 14, color: "#ef4444" }}
              />
              <span style={{ fontSize: 12, color: "#ef4444", flex: 1 }}>
                {error}
              </span>
              <button
                onClick={load}
                style={{
                  padding: "4px 10px",
                  border: "0.5px solid rgba(239,68,68,0.3)",
                  borderRadius: 6,
                  background: "#fff",
                  fontSize: 11,
                  cursor: "pointer",
                  color: "#ef4444",
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 16,
            }}
          >
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            ) : filtered.length === 0 ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 24px",
                  gap: 12,
                }}
              >
                <i
                  className="ti ti-layout-grid"
                  style={{ fontSize: 36, color: C.textFaint, opacity: 0.25 }}
                />
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: C.textMuted }}
                >
                  No templates found
                </span>
              </div>
            ) : (
              filtered.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  onPreview={setPreviewTemplate}
                  onUse={handleUse}
                />
              ))
            )}
          </div>
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>

      <PreviewModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onUse={handleUse}
      />
    </div>
  );
}
